import { gql } from './graphql'
import { setToken } from './auth'
import { applyTaxonomy, type Taxonomy } from './taxonomyStore'

export interface PromptHint {
  id: string
  kind: string
  dimension: string | null
  term: string | null
  start: number | null
  length: number | null
  suggestions: string[]
  message: string
}

export interface SuggestionCard {
  id: string
  kind: string
  dimension: string | null
  original: string | null
  suggestions: string[]
  reason: string
}

export interface PromptAnalysis {
  score: number
  coherence: number
  ready: boolean
  readback: string
  requiredDimensions: string[]
  covered: number
  total: number
  label: string
  hints: PromptHint[]
}

// The free rule-layer hints share the card shape, so they render through SuggestionCardList.
export function hintToCard(hint: PromptHint): SuggestionCard {
  return {
    id: hint.id,
    kind: hint.kind,
    dimension: hint.dimension,
    original: hint.term,
    suggestions: hint.suggestions,
    reason: hint.message,
  }
}

export interface ImproveResult {
  cards: SuggestionCard[]
  improved: string | null
  creditsCharged: number
  creditBalance: number | null
  signupRequired: boolean
  anonRemaining: number | null
  errors: string[]
}

export interface CurrentUser {
  id: string
  email: string
  plan: string
  admin: boolean
  creditAccount: { balance: number } | null
}

export interface Character {
  id: string
  slug: string
  name: string
  genre: string | null
  tagline: string | null
  suggestedStyle: string | null
}

export interface Project {
  id: string
  title: string | null
  styleText: string | null
  lyricsText: string | null
  character: { id: string } | null
  updatedAt: string
}

export interface LyricsResult {
  lyrics: string | null
  suggestedStyle: string | null
  creditsCharged: number
  creditBalance: number | null
  errors: string[]
}

const AUTH_FIELDS = `token user { id email plan admin creditAccount { balance } } errors`

export async function signUp(email: string, password: string): Promise<{ user: CurrentUser | null; errors: string[] }> {
  const data = await gql<{ signUp: { token: string | null; user: CurrentUser | null; errors: string[] } }>(
    `mutation($email: String!, $password: String!) { signUp(input: { email: $email, password: $password }) { ${AUTH_FIELDS} } }`,
    { email, password },
  )
  if (data.signUp.token) setToken(data.signUp.token)
  return { user: data.signUp.user, errors: data.signUp.errors }
}

export async function signIn(email: string, password: string): Promise<{ user: CurrentUser | null; errors: string[] }> {
  const data = await gql<{ signIn: { token: string | null; user: CurrentUser | null; errors: string[] } }>(
    `mutation($email: String!, $password: String!) { signIn(input: { email: $email, password: $password }) { ${AUTH_FIELDS} } }`,
    { email, password },
  )
  if (data.signIn.token) setToken(data.signIn.token)
  return { user: data.signIn.user, errors: data.signIn.errors }
}

export async function googleSignIn(idToken: string): Promise<{ user: CurrentUser | null; errors: string[] }> {
  const data = await gql<{ googleSignIn: { token: string | null; user: CurrentUser | null; errors: string[] } }>(
    `mutation($idToken: String!) { googleSignIn(input: { idToken: $idToken }) { ${AUTH_FIELDS} } }`,
    { idToken },
  )
  if (data.googleSignIn.token) setToken(data.googleSignIn.token)
  return { user: data.googleSignIn.user, errors: data.googleSignIn.errors }
}

export async function fetchMe(): Promise<CurrentUser | null> {
  const data = await gql<{ me: CurrentUser | null }>(
    `query { me { id email plan admin creditAccount { balance } } }`,
  )
  return data.me
}

const PROJECT_FIELDS = `id title styleText lyricsText character { id } updatedAt`

export async function fetchProjects(): Promise<Project[]> {
  const data = await gql<{ projects: Project[] }>(`query { projects { ${PROJECT_FIELDS} } }`)
  return data.projects
}

interface ProjectInput {
  title?: string | null
  styleText?: string | null
  lyricsText?: string | null
  characterId?: string | null
}

export async function createProject(input: ProjectInput): Promise<{ project: Project | null; errors: string[] }> {
  const data = await gql<{ createProject: { project: Project | null; errors: string[] } }>(
    `mutation($title: String, $styleText: String, $lyricsText: String, $characterId: ID) {
      createProject(input: { title: $title, styleText: $styleText, lyricsText: $lyricsText, characterId: $characterId }) {
        project { ${PROJECT_FIELDS} }
        errors
      }
    }`,
    { ...input },
  )
  return data.createProject
}

export async function updateProject(id: string, input: ProjectInput): Promise<{ project: Project | null; errors: string[] }> {
  const data = await gql<{ updateProject: { project: Project | null; errors: string[] } }>(
    `mutation($id: ID!, $title: String, $styleText: String, $lyricsText: String, $characterId: ID) {
      updateProject(input: { id: $id, title: $title, styleText: $styleText, lyricsText: $lyricsText, characterId: $characterId }) {
        project { ${PROJECT_FIELDS} }
        errors
      }
    }`,
    { id, ...input },
  )
  return data.updateProject
}

export async function deleteProject(id: string): Promise<string | null> {
  const data = await gql<{ deleteProject: { deletedId: string | null } }>(
    `mutation($id: ID!) { deleteProject(input: { id: $id }) { deletedId errors } }`,
    { id },
  )
  return data.deleteProject.deletedId
}

export interface Plan {
  key: string
  name: string
  monthlyCreditAllowance: number
  priceCents: number
}

export async function fetchPlans(): Promise<Plan[]> {
  const data = await gql<{ plans: Plan[] }>(
    `query { plans { key name monthlyCreditAllowance priceCents } }`,
  )
  return data.plans
}

export async function createCheckoutSession(planKey: string): Promise<{ url: string | null; errors: string[] }> {
  const data = await gql<{ createCheckoutSession: { url: string | null; errors: string[] } }>(
    `mutation($planKey: String!) { createCheckoutSession(input: { planKey: $planKey }) { url errors } }`,
    { planKey },
  )
  return data.createCheckoutSession
}

// Fetch the server's suggestion taxonomy (genre/mood/instrument families, popular sets,
// descriptions) and overlay it on the bundled default — so catalog content updates reach the app
// and extension via a server deploy, no rebuild. Runs at most once per session; on failure the
// bundled taxonomy stays in effect and a later call may retry. Returns a String (JSON) field to
// avoid bloating the GraphQL schema with the nested taxonomy shape.
let taxonomyLoaded = false
export async function loadSuggestionTaxonomy(): Promise<void> {
  if (taxonomyLoaded) return
  taxonomyLoaded = true
  try {
    const data = await gql<{ suggestionTaxonomy: string }>(`query { suggestionTaxonomy }`)
    applyTaxonomy(JSON.parse(data.suggestionTaxonomy) as Taxonomy)
  } catch {
    taxonomyLoaded = false
  }
}

export async function fetchPromptAnalysis(text: string, intent?: string): Promise<PromptAnalysis | null> {
  if (!text.trim()) return null
  const data = await gql<{ promptAnalysis: PromptAnalysis }>(
    `query($text: String!, $intent: String) {
      promptAnalysis(text: $text, intent: $intent) {
        score coherence ready readback requiredDimensions covered total label
        hints { id kind dimension term start length suggestions message }
      }
    }`,
    { text, intent },
  )
  return data.promptAnalysis
}

export async function fetchCharacters(): Promise<Character[]> {
  const data = await gql<{ characters: Character[] }>(
    `query { characters { id slug name genre tagline suggestedStyle } }`,
  )
  return data.characters
}

export async function generateLyrics(
  characterId: string,
  opts: { theme?: string; draft?: string },
): Promise<LyricsResult> {
  const data = await gql<{ generateLyrics: LyricsResult }>(
    `mutation($characterId: ID!, $theme: String, $draft: String) {
      generateLyrics(input: { characterId: $characterId, theme: $theme, draft: $draft }) {
        lyrics
        suggestedStyle
        creditsCharged
        creditBalance
        errors
      }
    }`,
    { characterId, theme: opts.theme, draft: opts.draft },
  )
  return data.generateLyrics
}

export async function improvePrompt(text: string): Promise<ImproveResult> {
  const data = await gql<{ improvePrompt: ImproveResult }>(
    `mutation($text: String!) {
      improvePrompt(input: { text: $text }) {
        cards { id kind dimension original suggestions reason }
        improved
        creditsCharged
        creditBalance
        signupRequired
        anonRemaining
        errors
      }
    }`,
    { text },
  )
  return data.improvePrompt
}
